import { IRoom } from '@eventr/common'
import {
    ClaimHide,
    ColoursObj,
    ImageRenderer,
    StyledPullToRefresh,
    Typography,
} from '@eventr/common-storybook'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { EventrLogo } from '../../assets/svgImages/Logo'
import { NoItineraryArrow } from '../../assets/svgImages/NoItineraryArrow'
import { BottomPageFab } from '../../home/components/bottomFab'
import { HeaderSearchBar } from '../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { query } from '../../state/headerState/headerSignals'
import { RocketChannel } from '../../state/rocketchat/rocketchat.interface'
import { selectRocketChatChannels } from '../../state/rocketchat/rocketchat.slice'
import { useRouteGuardContext } from '../../state/routeGuardProvider'
import { useThemeContext } from '../../state/themeState/themeContext'
import { keyboardState } from '../../state/windowState/windowSignals'
import { isConnectivityAvailable } from '../connectivity/connectivityListener'
import { withTierPermission } from '../editItinerary/HOC/withTierPermission'
import { navBarRef } from '../layout/navBar'
import { ChatCard } from './Components/ChatCard'
import { ChatCardSkeleton } from './Components/chatMenuSkeleton'
import { FadedText } from './Components/fadedText'
import { useRocketChatContext } from './rocketChatProvider'
interface PairedRoom {
    channel: RocketChannel
    room: IRoom
}
export const RocketChatMenu = () => {
    const { userRooms, isActivated } = useRocketChatContext()
    const themeImages = useThemeContext((s) => s.themeImages)
    const rocketchannels = useBoundStore(selectRocketChatChannels)
    const {
        noItineraries,
        checkIfAllDynamicSectionsEmpty,
        eventEnded,
        selectedItinID,
    } = useBoundStore(
        useShallow((s) => ({
            users: s.users,
            selectedItinID: s.selectedItinID,
            noItineraries: s.noItineraries,
            checkIfAllDynamicSectionsEmpty: s.checkIfAllDynamicSectionsEmpty,
            eventEnded: s.eventEnded,
        }))
    )

    // Cache for rooms that persists between updates
    const roomsCache = useRef<{ channel: RocketChannel; room: IRoom }[]>([])

    // Instead of using filteredRooms directly in rendering, use a displayRooms state
    const [displayRooms, setDisplayRooms] = useState<PairedRoom[]>([])

    // Determine if we're loading new data
    const [isLoading, setIsLoading] = useState(false)
    const { isAllowed } = useRouteGuardContext()
    const navbarHeight = navBarRef.value?.current?.clientHeight ?? 0
    // Effect to handle itinerary changes
    useEffect(() => {
        // When the selected itinerary changes
        setIsLoading(true)
        console.log(
            `🚀 RocketChatMenu: Itinerary changed to ${selectedItinID}, loading new data...`
        )

        // Start a timer - if loading takes too long, show loading state
        const loadingTimer = setTimeout(() => {
            // Only show loading if we still don't have data after a delay

            if (!rocketchannels?.length || !userRooms?.length) {
                setDisplayRooms([])
            }
        }, 1000) // Wait 1 second before showing empty state

        return () => {
            clearTimeout(loadingTimer)
        }
    }, [selectedItinID, rocketchannels, userRooms])

    // Effect to pair channels with rooms and sort by latest activity
    useEffect(() => {
        // Skip processing if we don't have necessary data
        if (!rocketchannels?.length || !userRooms?.length) {
            return
        }

        try {
            // Create a lookup map of rooms by rid for efficient matching
            const roomsMap = new Map<string, IRoom>()
            userRooms.forEach((room) => {
                if (room._id) {
                    roomsMap.set(room._id, room)
                }
            })

            // Match channels with rooms and filter by search query
            const pairedRooms = rocketchannels
                .map((channel) => {
                    const matchingRoom = roomsMap.get(channel?.channel_id ?? '')
                    return matchingRoom ? { channel, room: matchingRoom } : null
                })
                .filter((pair): pair is PairedRoom => {
                    if (!pair) return false

                    // Apply search filter if query exists
                    if (query.value && query.value.length > 0) {
                        const searchTerm = query.value.toLowerCase()
                        const roomName =
                            pair.room.fname?.toLowerCase() ||
                            pair.room.name?.toLowerCase() ||
                            ''

                        // Also search in channel name if available
                        const channelName =
                            pair.channel?.json_data?.channel_name?.toLowerCase() ||
                            ''

                        // Include if name contains search term
                        return (
                            roomName.includes(searchTerm) ||
                            channelName.includes(searchTerm)
                        )
                    }

                    return true
                })
                .sort((a, b) => {
                    // Sort by latest activity (message timestamp or creation date)

                    // Get the most recent timestamp for each room
                    // _updatedAt is usually set when a message is received
                    const aLatestActivity =
                        a?.room?.lm?.['$date'] || a?.room?.ts?.['$date']
                    const bLatestActivity =
                        b?.room?.lm?.['$date'] || b?.room?.ts?.['$date']

                    // Compare timestamps (newer timestamps come first)
                    if (aLatestActivity && bLatestActivity) {
                        return (
                            new Date(bLatestActivity).getTime() -
                            new Date(aLatestActivity).getTime()
                        )
                    }

                    // If only one has a timestamp
                    if (aLatestActivity) return 1
                    if (bLatestActivity) return -1

                    return 0
                })

            // Update both the filtered rooms and the cache
            roomsCache.current = pairedRooms

            // Update the display rooms (which is what gets rendered)
            setDisplayRooms(pairedRooms)

            // No longer loading
            setIsLoading(false)
        } catch (error) {
            console.error('Error pairing channels and rooms:', error)
            setIsLoading(false)
        }
    }, [query.value, rocketchannels, userRooms])

    const renderChats = useCallback(() => {
        if (displayRooms.length === 0) {
            return null
        }

        return displayRooms.map((pair, index) => {
            return (
                <ChatCard room={pair.room} channel={pair.channel} key={index} />
            )
        })
    }, [displayRooms])

    const AddGroupButton = withTierPermission(BottomPageFab, {
        requiredTier: ['enterprise'],
    })

    const renderChannelsList = useCallback(() => {
        // If we have previous data but are loading new data, show the previous data with a loading overlay
        if (isLoading && roomsCache.current.length > 0) {
            return (
                <Stack
                    paddingBottom={
                        navBarRef.value?.current?.offsetHeight + 'px' || '56px'
                    }
                    overflow="scroll"
                    height="100%"
                    position="relative"
                >
                    {/* Semi-transparent loading overlay */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bgcolor="rgba(255, 255, 255, 0.7)"
                        zIndex={1}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <CircularProgress />
                    </Box>

                    <Stack width={'100vw'}>{renderChats()}</Stack>
                </Stack>
            )
        }
        // If we have no cached data and are loading, show skeletons
        if (!userRooms || (isLoading && displayRooms.length === 0)) {
            return (
                <Stack
                    paddingBottom={
                        navBarRef.value?.current?.offsetHeight + 'px' || '56px'
                    }
                    overflow="scroll"
                    height="100%"
                >
                    <Stack width={'100vw'}>
                        {/* Show 5 skeleton items */}
                        {Array(5)
                            .fill(0)
                            .map((_, i) => (
                                <ChatCardSkeleton key={i} />
                            ))}
                    </Stack>
                </Stack>
            )
        }
        // Otherwise show the normal list
        return (
            <Stack
                paddingBottom={
                    navBarRef.value?.current?.offsetHeight + 'px' || '56px'
                }
                overflow="scroll"
                height="100%"
            >
                <Stack width={'100vw'}>
                    {displayRooms.length === 0 ? (
                        query.value.length === 0 ? (
                            <FadedText text="No active channels, try creating one!" />
                        ) : (
                            <FadedText text="No Results" />
                        )
                    ) : (
                        renderChats()
                    )}
                </Stack>
            </Stack>
        )
    }, [renderChats, displayRooms, userRooms, isLoading])

    return !noItineraries ? (
        <div>
            {!isActivated && isActivated !== undefined ? (
                <Box
                    padding="16px"
                    gap="24px"
                    height={`calc(85vh - ${navbarHeight}px)`}
                >
                    <Stack
                        width="100%"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                        paddingTop="25px"
                    >
                        <Typography
                            id="addTeamMembersChatPageTitle"
                            variant="desktopH3"
                            align="left"
                            gap="24px"
                            textAlign="center"
                            paddingBottom="10px"
                            color={ColoursObj.core.primary}
                        >
                            {i18n.t('chat.empty-state.add-team')}
                        </Typography>
                        <ImageRenderer
                            variant="chatEmpty"
                            src={themeImages?.chatEmpty}
                        />
                        <Typography
                            id="chatPageInvitePeopleSubText"
                            variant="bodyBold"
                            color={ColoursObj.core.secondary}
                            paddingTop="20px"
                            textAlign="center"
                        >
                            {i18n.t('chat.empty-state.invite-people')}
                        </Typography>
                        {keyboardState.value.openState === 'didHide' && (
                            <div
                                style={{
                                    position: 'fixed',
                                    left: '160px',
                                    bottom:
                                        10 +
                                        (navBarRef.value?.current
                                            ?.clientHeight ?? 0) +
                                        'px',
                                }}
                            >
                                <div style={{ transform: 'scaleX(-1)' }}>
                                    <NoItineraryArrow />
                                </div>
                            </div>
                        )}
                    </Stack>
                </Box>
            ) : (
                <Stack bgcolor={ColoursObj.backgrounds.primary}>
                    {!isConnectivityAvailable.value && (
                        <Typography
                            justifyContent="center"
                            style={{
                                padding: '8px',
                                display: 'flex',
                                backgroundColor: ColoursObj.states.warningLight,
                            }}
                        >
                            {i18n.t('rocketChat.offlineChat')}
                        </Typography>
                    )}
                    <Box padding="0px 8px">
                        <HeaderSearchBar />
                    </Box>
                    <StyledPullToRefresh
                        onRefresh={async () => {
                            /// Nothing
                        }}
                        promiseFunction={async () => {
                            await bs().pullRocketChatChannels()
                        }}
                        maxPullDownDistance={100}
                    >
                        {renderChannelsList()}
                    </StyledPullToRefresh>
                    <ClaimHide paths={['/']} claims={['manageGroupChat']}>
                        <AddGroupButton
                            id="addGroupButton"
                            onClick={() => {
                                window.pushPath(
                                    '/tabs/rocketChat/createGroupChannel'
                                )
                            }}
                            disabled={!isConnectivityAvailable.value}
                            icon="Add_Group"
                        />
                    </ClaimHide>
                </Stack>
            )}
        </div>
    ) : (
        <div>
            <Stack width="100%" padding="0px 8px 10%">
                <EventrLogo />
            </Stack>

            <Box padding="16px" gap="24px">
                <Stack
                    width="100%"
                    height="100%"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    gap="24px"
                >
                    <Typography
                        id="noItineraryStateChatPageTitle"
                        variant="mobileH3"
                        align="center"
                        color={ColoursObj.core.primary}
                    >
                        {i18n.t('chat.empty-state.chat-lives')}
                    </Typography>
                    <ImageRenderer
                        variant="chatEmpty"
                        src={themeImages?.chatEmpty}
                    />
                    <Typography
                        id="noItineraryStateChatSubText"
                        color={ColoursObj.core.secondary}
                        paddingTop="10px"
                        align="center"
                        fontWeight="600"
                    >
                        {isAllowed(['allowedEdit']) ? (
                            <>{i18n.t('chat.empty-state.return-home')}</>
                        ) : (
                            <>{i18n.t('chat.empty-state.no-edit-permission')}</>
                        )}
                    </Typography>
                </Stack>
            </Box>
        </div>
    )
}
